import { Link } from 'react-router';

import { FreemiumOnboardingState } from '@/hooks/useFreemiumOnboarding';

interface FreemiumOnboardingWizardProps {
  /**
   * Pre-derived onboarding state supplied by the caller. The CheckAuth gate
   * already computes `useFreemiumOnboarding()`, so it passes the phase/status
   * straight through — the wizard is a pure presentational component and never
   * re-runs the hook (or its queries).
   */
  state: Pick<FreemiumOnboardingState, 'phase' | 'phaseStatus'>;
  /**
   * Tenant-scoped logout path (e.g. `/:userId/logout`), pre-built by the
   * CheckAuth gate which owns the routing context (`me`, `encode`,
   * `generatePath`). This full-screen gate replaces the entire app chrome —
   * including the AccountDropdown that normally hosts sign-out — so the wizard
   * MUST surface its own discoverable escape or a gated owner has no visible
   * way to sign out (they'd have to hand-type the /logout URL). The `/logout`
   * route is already exempted from the gate in CheckAuth, so this link resolves.
   */
  logoutPath: string;
}

/**
 * Minimal full-screen placeholder rendered by the CheckAuth gate while a
 * freemium tenant's onboarding is incomplete. ENG-4178 replaces the internals
 * with the real 3-phase wizard shell; this stub only proves the gate wiring and
 * keeps the seam testable.
 */
export function FreemiumOnboardingWizard({
  state,
  logoutPath,
}: FreemiumOnboardingWizardProps) {
  const { phase, phaseStatus } = state;

  return (
    <div className="bg-chariot-background-primary fixed inset-0 z-50 flex items-center justify-center">
      <div className="border-chariot-stroke bg-chariot-background-secondary flex flex-col items-center gap-2 rounded-lg border p-8 text-center">
        <h1 className="text-chariot-text-primary text-xl font-semibold">
          Onboarding
        </h1>
        <p className="text-chariot-text-secondary text-sm">
          Phase {phase} / 3 &middot; {phaseStatus}
        </p>
        <Link
          to={logoutPath}
          className="text-chariot-text-secondary mt-2 text-xs underline"
        >
          Sign out
        </Link>
      </div>
    </div>
  );
}

export default FreemiumOnboardingWizard;
